import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, UserRole } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isParceiro: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Inicializar estados diretamente do localStorage para evitar flash de loading
  const [user, setUser] = useState<User | null>(() => authService.getStoredUser());
  const [token, setToken] = useState<string | null>(() => authService.getStoredToken());
  const [isLoading, setIsLoading] = useState(true);

  // Função utilitária para limpar sessão
  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  // Verifica se um token JWT está expirado decodificando o payload
  const isTokenExpired = useCallback((jwtToken: string): boolean => {
    try {
      const parts = jwtToken.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) return false; // Sem expiração definida
      // exp é em segundos, Date.now() em milissegundos
      return payload.exp * 1000 < Date.now();
    } catch {
      return true; // Token malformado é considerado expirado
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = authService.getStoredToken();
      const storedUser = authService.getStoredUser();

      // Se não há token ou usuário armazenado, limpa e finaliza
      if (!storedToken || !storedUser) {
        clearSession();
        setIsLoading(false);
        return;
      }

      // Verificar se o token já está expirado no client-side
      if (isTokenExpired(storedToken)) {
        console.warn('Token expirado, limpando sessão.');
        clearSession();
        setIsLoading(false);
        return;
      }

      // Token e usuário existem e não estão expirados
      // Validar token com o servidor
      try {
        const response = await authService.me();
        if (response.success && response.data) {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } else {
          // Resposta sem sucesso - sessão inválida
          clearSession();
        }
      } catch (error: any) {
        // Para QUALQUER erro de autenticação, limpar a sessão
        // Isso inclui 401, 403, e outros erros de servidor
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.warn('Sessão inválida, redirecionando para login.');
          clearSession();
        } else {
          // Para erros de rede/servidor, manter sessão temporariamente
          // mas apenas se o token ainda não expirou
          console.warn('Erro ao validar token, mantendo sessão temporariamente:', error.message);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [clearSession, isTokenExpired]);

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password });

    if (response.success && response.data) {
      const { token: newToken, user: newUser } = response.data;

      // Verificar se o usuário é PARCEIRO ou ADMIN
      if (newUser.nivelAcesso !== UserRole.PARCEIRO && newUser.nivelAcesso !== UserRole.ADMIN) {
        throw new Error('Acesso restrito a postos parceiros');
      }

      authService.setAuth(newToken, newUser);
      setToken(newToken);
      setUser(newUser);
    } else {
      throw new Error(response.message || 'Erro ao fazer login');
    }
  };

  const logout = async () => {
    await authService.logout();
    clearSession();
  };

  const isParceiro = user?.nivelAcesso === UserRole.PARCEIRO || user?.nivelAcesso === UserRole.ADMIN;
  const isAdmin = user?.nivelAcesso === UserRole.ADMIN;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        login,
        logout,
        isParceiro,
        isAdmin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
